import {INFLUENCER_KEYS} from "@/api/influencerApi";
import {MEDIA_KEYS} from "@/api/mediaApi";
import InfluencerMediaForAdmin from "@/components/Account/InfluencerMediaForAdmin";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import TextareaWithLinks from "@/components/Common/forms/TextareaWithLinks";
import Loader from "@/components/Common/kpi/Loader";
import type {InfluencerListInstagramMediaSerializer, OverallTikTokFeedSerializer} from "@/generated/DjangoTypes";
import useDownloadWithProgress from "@/hooks/useDownloadWithProgress";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {useCategoryStore} from "@/store/category";
import {shouldMuteVideosAtom} from "@/store/jotaiAtoms";
import type {TMediaManagerContent, TPaginated} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import {createFileRoute, Link} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import type {AxiosResponse} from "axios";
import {produce} from "immer";
import {useAtom} from "jotai";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useKeyPressEvent, useTitle} from "react-use";
import {z} from "zod";

const ALL_RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const Route = createFileRoute("/_authenticated/_admin/admins/tiktok-media-manager")({
  component: TiktokMediaManager,
  validateSearch: zodValidator(
    z.object({
      campaign_ids: z.string().optional(),
      campaign_name: z.string().optional(),
      hide_rated: z.string().optional(),
      interests: z.string().optional(),
      media_ids: z.string().optional(),
      quality_ratings: z.string().optional(),
      tiktok_name: z.string().optional(),
    }),
  ),
});

function TiktokMediaManager() {
  const {t} = useTranslation("navigation");

  useTitle(t("tiktok_media_manager"));

  const search = Route.useSearch();
  const defaultMediaIds = new URLSearchParams(search).get("media_ids") ?? "";
  const {downloadUrl, isDownloading, progress} = useDownloadWithProgress();
  const categories = useCategoryStore(state => state.adminCategories);
  const [shouldMuteVideos, setShouldMuteVideos] = useAtom(shouldMuteVideosAtom);
  const [campaignIds, setCampaignIds] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [hideRated, setHideRated] = useState(false);
  const [interests, setInterests] = useState<number[]>([]);
  const [mediaIds, setMediaIds] = useState(defaultMediaIds);
  const [message, setMessage] = useState("");
  const [prevShowedIndex, setPrevShowedIndex] = useState<number>(0);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [qualityScore, setQualityScore] = useState(0);
  const [selectedInterests, setSelectedInterests] = useState<number[]>([]);
  const [selectedQualityRatings, setSelectedQualityRatings] = useState<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [tiktokName, setTiktokName] = useState("");
  const [title, setTitle] = useState("Content Feedback von Refluenced");
  const [viewedContent, setViewedContent] = useState<(TMediaManagerContent & {index: number}) | null>(null);
  const [params, setParams] = useState(
    new URLSearchParams({
      campaign_ids: campaignIds,
      campaign_name: campaignName,
      media_ids: mediaIds,
      tiktok_name: tiktokName,
    }),
  );

  const tiktokFeedQuery = useGenericInfiniteQuery<OverallTikTokFeedSerializer>({
    queryKey: INFLUENCER_KEYS.qInfluencerTikTokFeed(params.toString()),
  });
  const content = tiktokFeedQuery.data;
  const mappedContent = (content ?? []).map(contentInformation => ({
    id: contentInformation.id?.toString(),
    mediaId: contentInformation.media_id,
    videoUrl: contentInformation.video?.url!,
    thumbnailUrl: contentInformation.thumbnail?.url!,
    permalink: contentInformation.permalink,
    influencer: contentInformation.influencer,
    campaignApplication: contentInformation.campaign_application,
    media_type: "VIDEO" as "VIDEO",
    media_product_type: "TIKTOK" as "TIKTOK",
    highlighted: contentInformation.highlighted,
    interests: contentInformation.interests,
    quality_score: contentInformation.quality_score,
  }));
  const {ref} = useInViewWithQuery(tiktokFeedQuery);

  const setViewedContentFromIndex = (index: number) => {
    const content = mappedContent.at(index);
    if (!content) return;
    const newIndex = mappedContent.indexOf(content);
    if (newIndex === -1) return;
    if (newIndex >= mappedContent.length - 3 && tiktokFeedQuery.hasNextPage) tiktokFeedQuery.fetchNextPage();
    setViewedContent({...content, index: newIndex});
  };
  const handleNextContent = () => {
    if (!viewedContent || showFeedback) return;
    setViewedContentFromIndex(viewedContent.index + 1);
    setPrevShowedIndex(viewedContent.index + 1);
  };
  const handlePrevContent = () => {
    if (!viewedContent || showFeedback) return;
    setViewedContentFromIndex(viewedContent.index - 1);
    setPrevShowedIndex(viewedContent.index - 1);
  };
  const onDownload = (content: TMediaManagerContent) => {
    const url = content.media_type === "VIDEO" ? content.videoUrl : content.thumbnailUrl;
    if (!url) return;
    downloadUrl({filename: content.mediaId + "." + url.split(".").at(-1)?.split("?")[0], url});
  };

  useKeyPressEvent("ArrowLeft", handlePrevContent);
  useKeyPressEvent("ArrowRight", handleNextContent);

  useEffect(() => {
    if (!viewedContent) return;
    setQualityScore(content![viewedContent.index].quality_score ?? 0);
    setInterests(content![viewedContent.index].interests ?? []);
    setPrevShowedIndex(viewedContent.index);
  }, [content, viewedContent]);

  return (
    <div className="max-w-wide relative self-center px-5 py-8 md:px-8">
      <div className="pb-8 text-6xl font-medium">TikTok Media Manager</div>
      <div className="pb-8 text-xl">
        Here you can manage all of the TikTok media what the users uploaded. You can do the following here:
        <ul className="list-disc">
          <li>Highlight / unhighlight</li>
          <li>Delete media</li>
          <li>Change campaign</li>
        </ul>
      </div>
      <div className="pb-8">
        <div className="pb-2 text-2xl font-medium">Search for</div>
        <div className="ml-2 flex flex-wrap items-center gap-x-2 gap-y-4">
          <div className="flex items-center gap-2">
            TikTok name
            <input className="border-2 border-black p-2" onChange={input => setTiktokName(input.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            Campaign name
            <input className="border-2 border-black p-2" onChange={input => setCampaignName(input.target.value)} />
          </div>
        </div>
        <div className="mt-4 ml-2 flex items-center gap-2">
          Media ids (comma separated, e.g: 4,1,3)
          <input
            className="border-2 border-black p-2"
            defaultValue={mediaIds}
            onChange={input => setMediaIds(input.target.value)}
          />
        </div>
        <div className="mt-4 ml-2 flex items-center gap-2">
          Campaing ids (comma separated, e.g: 4,1,3)
          <input className="border-2 border-black p-2" onChange={input => setCampaignIds(input.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-neutral-black text-xs">Interests</span>
          <div className="flex flex-row flex-wrap gap-x-2 gap-y-1">
            {categories.map(category => (
              <button
                onClick={() => {
                  setSelectedInterests(state =>
                    state.includes(category.id) ? state.filter(b => b !== category.id) : [...state, category.id],
                  );
                }}
                key={category.id}
                className={`rounded-full border px-2 py-1 text-xs font-semibold ${
                  selectedInterests.includes(category.id)
                    ? "bg-primary-dark-solid text-white"
                    : "text-primary-dark-solid bg-white"
                }`}>
                {category.name}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 ml-2 flex flex-col gap-2">
          <div className="text-sm font-medium text-gray-700">Quality Ratings</div>
          <div className="flex flex-wrap gap-2">
            {ALL_RATINGS.map(rating => (
              <button
                key={rating}
                className={`rounded-md border border-gray-300 px-4 py-2 text-sm ${
                  selectedQualityRatings.includes(rating) ? "bg-purple-500 text-white" : "bg-white text-gray-700"
                } hover:bg-purple-300`}
                onClick={() => {
                  setSelectedQualityRatings(prevRatings => {
                    const newRatings = prevRatings.includes(rating)
                      ? prevRatings.filter(r => r !== rating)
                      : [...prevRatings, rating];
                    return newRatings.sort((a, b) => a - b);
                  });
                }}>
                {rating}
              </button>
            ))}
          </div>
          <div className="mt-4 mb-2">
            <button
              className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                selectedQualityRatings.length === ALL_RATINGS.length
                  ? "bg-gray-500 text-white hover:bg-gray-600"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
              onClick={() => {
                if (selectedQualityRatings.length === ALL_RATINGS.length) {
                  setSelectedQualityRatings([]);
                } else {
                  setSelectedQualityRatings(ALL_RATINGS);
                }
              }}>
              {selectedQualityRatings.length === ALL_RATINGS.length ? "Deselect All" : "Select All"}
            </button>
          </div>
        </div>
        <div className="mt-4 ml-2 flex items-center gap-2">
          Hide already rated medias
          <input
            type="checkbox"
            className="border-2 border-black p-2"
            onChange={input => setHideRated(input.currentTarget.checked)}
          />
        </div>
        <button
          className="my-4 rounded-sm bg-green-700 px-4 py-2 font-bold text-white hover:bg-green-900"
          onClick={() => {
            const newParams = new URLSearchParams({
              tiktok_name: tiktokName,
              campaign_name: campaignName,
              media_ids: mediaIds,
              campaign_ids: campaignIds,
            });

            if (hideRated) newParams.append("hide_rated", "true");
            if (selectedQualityRatings.length > 0) {
              newParams.append("quality_ratings", selectedQualityRatings.join(","));
            }
            if (selectedInterests.length > 0) {
              newParams.append("interests", selectedInterests.join(","));
            }

            setParams(newParams);
          }}>
          Filter
        </button>
      </div>
      {!content && <Loader />}
      {content && content.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3">
          {mappedContent.map((content, index) => (
            <InfluencerMediaForAdmin
              key={content.id}
              incoming_content={content}
              openContentViewer={() => setViewedContent({...content, index})}
              onDownload={() => onDownload(content)}
              isTikTok
            />
          ))}
          {tiktokFeedQuery.isFetching && <Loader />}
          <div ref={ref} />
        </div>
      )}
      {showFeedback && (
        <Modal
          modalTitle="Feedback"
          customBottomRow={<></>}
          onClose={() => {
            setShowFeedback(false);
            setTitle("Content Feedback von Refluenced");
            setMessage("");
          }}
          className="p-10">
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700">
              Quality score (between 1 and 10):{" "}
              <input
                type="number"
                className="focus:border-primary-medium-solid w-16 rounded-md border border-gray-300 p-2 shadow-xs outline-hidden"
                value={qualityScore || undefined}
                onChange={e => {
                  const value = +e.target.value;
                  setQualityScore(Math.max(1, Math.min(10, value)));
                }}
              />
            </label>
          </div>
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700">
              Title
              <input
                type="text"
                className="focus:border-primary-medium-solid mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-xs outline-hidden"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </label>
          </div>
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700">
              Message
              <TextareaWithLinks value={message} onChange={setMessage} />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              disabled={!qualityScore || !title || !message}
              className="disabled:bg-neutral-white-dark disabled:text-neutral-grey-light rounded-sm bg-blue-500 px-4 py-2 text-white hover:enabled:bg-blue-700"
              onClick={async () => {
                try {
                  if (!content?.[prevShowedIndex]) return;

                  await client.post(MEDIA_KEYS.mContentReview[0], {
                    tiktok_media_id: content[prevShowedIndex].id,
                    influencer_id: content[prevShowedIndex].influencer.uuid,
                    title: title,
                    feedback: message,
                    quality_score: qualityScore,
                  });
                  queryClient.setQueryData<
                    InfiniteData<AxiosResponse<TPaginated<InfluencerListInstagramMediaSerializer>>>
                  >(INFLUENCER_KEYS.qInfluencerTikTokFeed(params.toString()), prevState => {
                    if (!prevState) return prevState;

                    return produce(prevState, draft => {
                      for (const page of draft.pages) {
                        for (const mediaItem of page.data.results) {
                          if (mediaItem.media_id === content![viewedContent!.index].media_id) {
                            mediaItem.quality_score = qualityScore;
                          }
                        }
                      }
                    });
                  });

                  toast("Feedback sent successfully!", {type: "success"});
                  setShowFeedback(false);
                  setViewedContentFromIndex(prevShowedIndex);
                  setTitle("Content Feedback von Refluenced");
                  setMessage("");
                } catch (error) {
                  toast("Something went wrong while sending the feedback!", {
                    type: "error",
                  });
                  console.error(error);
                }
              }}>
              Send
            </button>
          </div>
        </Modal>
      )}
      {viewedContent && (
        <Modal
          className="px-0"
          backgroundClassName="md:w-[80vw] 2xl:w-[65vw]"
          onClose={() => {
            if (showFeedback) return;
            setViewedContent(null);
          }}
          customTitleRow={
            <div className="flex w-full items-center justify-between pr-5">
              <Link
                to="/influencer/$influencerId"
                params={{influencerId: viewedContent.influencer.uuid!}}
                target="_blank">
                <div className="flex p-4 text-lg font-medium underline">
                  <img
                    className="mr-2 size-8 rounded-sm"
                    src={(viewedContent.influencer.picture320 ?? viewedContent.influencer.picture)!}
                    alt="Default avatar"
                  />
                  {viewedContent.influencer.first_name} {viewedContent.influencer.last_name} (
                  {viewedContent.influencer.tiktok_username})
                </div>
              </Link>
              <div className="flex gap-2">
                <span>
                  {viewedContent.index + 1} / {mappedContent.length}
                </span>
              </div>
            </div>
          }
          customBottomRow={
            <div className="flex flex-col items-end">
              <div className="m-3 flex space-y-4 md:flex-row md:space-y-0 md:space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setQualityOpen(!qualityOpen);
                  }}
                  className="h-14 w-40 rounded-sm bg-blue-400 px-4 py-2 font-bold text-white hover:bg-blue-600">
                  {!qualityOpen ? "Show Rating" : "Hide Rating"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowFeedback(true);
                  }}
                  className="h-14 w-40 rounded-sm bg-purple-500 px-4 py-2 font-bold text-white hover:bg-purple-700">
                  Feedback
                </button>
                <Button
                  buttonStyle="Custom"
                  isLoading={isDownloading}
                  progress={progress}
                  shouldScaleOnHover={false}
                  onClick={() => onDownload(viewedContent)}
                  className="h-14 w-40 rounded-sm bg-green-500 px-4 py-2 font-bold text-white hover:bg-green-800">
                  Download
                </Button>
              </div>
              {qualityOpen && (
                <div className="m-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-neutral-black font-normal">
                      Here you can change the quality score and the interests (categories).
                    </p>
                    <div>
                      <label className="flex flex-row text-black">
                        Quality score (between 1 and 10):
                        <input
                          className="mx-3 border border-black px-2"
                          type="number"
                          min="1"
                          max="10"
                          value={qualityScore ? +qualityScore : ""}
                          onChange={e => {
                            setQualityScore(+e.target.value);
                          }}
                        />
                      </label>
                    </div>
                    <div>
                      <label className="flex flex-row text-black">
                        Categories (Multi select, press Ctrl):&nbsp;
                        <select
                          id="interests"
                          className="h-48 rounded-md border border-gray-300 px-4 py-2"
                          value={interests?.map(interest => interest.toString())}
                          onChange={e => {
                            const selectedIds = Array.from(e.target.selectedOptions, option => Number(option.value));
                            setInterests(selectedIds);
                          }}
                          multiple>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <p className="text-neutral-black font-normal">
                      You can change it anytime in the future, but once you press the 'Save', it will be saved to the
                      database.
                    </p>
                    <div>
                      <button
                        className="rounded-sm bg-gray-500 px-4 py-2 font-bold text-white hover:bg-gray-800"
                        onClick={async () => {
                          const url = `/tiktok/${content![viewedContent!.index].id}/`;
                          await client.patch(url, {quality_score: qualityScore, interests});
                          queryClient.setQueryData<
                            InfiniteData<AxiosResponse<TPaginated<InfluencerListInstagramMediaSerializer>>>
                          >(INFLUENCER_KEYS.qInfluencerTikTokFeed(params.toString()), prevState => {
                            if (!prevState) return prevState;

                            return produce(prevState, draft => {
                              for (const page of draft.pages) {
                                for (const mediaItem of page.data.results) {
                                  if (mediaItem.media_id === content![viewedContent!.index].media_id) {
                                    mediaItem.quality_score = qualityScore;
                                    mediaItem.interests = interests;
                                  }
                                }
                              }
                            });
                          });
                        }}>
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          }>
          <div className="flex items-center justify-between gap-3 px-3">
            <button
              type="button"
              className="bg-primary-light-solid hover:bg-primary-medium-solid size-10 rounded-xs text-center text-2xl leading-tight"
              onClick={handlePrevContent}>
              {"<"}
            </button>

            <video
              onContextMenu={e => e.preventDefault()}
              onMouseDown={e => e.preventDefault()}
              autoPlay
              loop
              controls
              src={viewedContent.videoUrl!}
              className="max-h-[60vh] max-w-[60vw] rounded-md bg-black object-contain"
              muted={shouldMuteVideos}
              onVolumeChange={e => setShouldMuteVideos(e.currentTarget.muted || e.currentTarget.volume === 0)}
            />

            <button
              type="button"
              className="bg-primary-light-solid hover:bg-primary-medium-solid size-10 rounded-xs text-center text-2xl leading-tight"
              onClick={handleNextContent}>
              {">"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
